import React from "react";
import {
  Typography,
} from "@material-tailwind/react";
import { StatisticsCard } from "@/widgets/cards";
import { UserGroupIcon, ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import { getDashboardCounts, downloadDashboardReport } from "@/store/slices/homeSlice";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import useCountUp from "@/components/UpCount";
import { useNavigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";
import DashboardGraph from "./dashboard-graph";
import CountryDetection from "@/components/CountryDetection";
import { handleDownloadURL } from "@/components/DownloadFile";

export function Home() {
  const { t } = useTranslation("home");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [fromDate, setFromDate] = React.useState("");
  const [toDate, setToDate] = React.useState("");

  const { dashboardData, loading, downloadLoading } = useSelector((state) => state.home);
  const { user } = useSelector((state) => state.user);

  const currentUserRole = user?.roles?.[0];
  const canDownload = ['Administrator', 'Sub Admin', 'Finance'].includes(currentUserRole);

  useEffect(() => {
    dispatch(getDashboardCounts({ fromDate, toDate }));
  }, [dispatch, fromDate, toDate]);

  useEffect(() => {
    if (fromDate && toDate && fromDate > toDate) {
      setToDate(""); // reset invalid value
    }
  }, [fromDate]);

  const handleDownloadReport = async () => {
    try {
      const resultAction = await dispatch(downloadDashboardReport({ from_date: fromDate, to_date: toDate })).unwrap();
      
      if (resultAction?.status && resultAction?.data?.download_url) {
        await handleDownloadURL({
          fileUrl: resultAction.data.download_url,
        });
      }
    } catch (err) {
      console.error("Failed to download report:", err);
    }
  };

  const cardsData = dashboardData?.map(dataItem => {
    const userRole = dataItem.user_type || "User";

    return {
      color: "gray",
      icon: UserGroupIcon,
      title: `${userRole}`,
      value: dataItem.new_users ?? "0",
      footer: {
        color: "text-green-500",
        value: dataItem.total ?? "0",
        label: t('total', { userRole }),
      },
      user_role: userRole
    };
  }).filter(card => card !== null);

  const goToPage = (user_role) => {
    const urlMap = {
      'CR': 'city-representative',
      'SR': 'sales-representative',
      'Finance': 'finance',
      'Sub Admin': 'sub-admin',
      'Delivery Partner': 'delivery-partner',
      'Procurement': 'procurement',
      'Customer': 'customers',
      'Vendor': 'vendors',
      'Order': 'orders',
      'RFQ': 'rfqs',
      'Pending Orders': 'rfqs',
      'Accepted Quotation': 'rfqs',
      'Rejected Quotation': 'rfqs',
      'Pending By Sales': 'rfqs',
    };
    const url = urlMap[user_role] ?? null;
    if (url) {
      navigate(`/dashboard/${url}`);
    }
  }

  return (
    <div className="mt-5 mb-8 flex flex-col gap-4">
      <ScrollToTop />

      <div className="flex flex-col">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 px-4 md:px-6">

          {/* Left Side */}
          <div>
            <Typography variant="h5" color="blue-gray">
              {t("dashboard")}
            </Typography>
          </div>

          {/* Right Side */}
          <div className="flex flex-wrap items-center gap-3">

            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                    {t("from")}
                  </label>

                  <input
                    type="date"
                    value={fromDate}
                    max={toDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="text-sm text-gray-700 bg-transparent border-none outline-none cursor-pointer w-32"
                  />
                </div>

                <div className="h-6 w-px bg-gray-200" />

                <div className="flex flex-col">
                  <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                    {t("to")}
                  </label>

                  <input
                    type="date"
                    value={toDate}
                    min={fromDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="text-sm text-gray-700 bg-transparent border-none outline-none cursor-pointer w-32"
                  />
                </div>
              </div>
            </div>

            {(fromDate || toDate) && (
              <button
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                }}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>

                {t("clear")}
              </button>
            )}

            {/* Black Download Button (Role Restricted) */}
            {canDownload && (
              <button
                onClick={handleDownloadReport}
                disabled={downloadLoading}
                className="flex items-center gap-2 bg-black hover:bg-gray-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-sm transition-all duration-200 disabled:opacity-50"
                title="Download Report"
              >
                <ArrowDownTrayIcon className="w-4 h-4 text-white" />
                <span>{downloadLoading ? "Downloading..." : t("download_report", "Download Report")}</span>
              </button>
            )}

          </div>

        </div>
        {(loading && dashboardData === null) && <p className="text-center mt-10">
          <div role="status">
            <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-green-500" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
              <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
            </svg>
          </div>
        </p>}

        {(user?.roles?.[0] === 'Sub Admin' || user?.roles?.[0] === 'Finance' || user?.roles?.[0] === 'Administrator') && (
          <DashboardGraph fromDate={fromDate} toDate={toDate} />
        )}

        <div className="mb-12 grid gap-y-10 gap-x-6 md:grid-cols-2 xl:grid-cols-4 px-4 md:px-6">
          {cardsData?.map((card) => (
            <div key={card.title} onClick={() => goToPage(card.user_role)} className="cursor-pointer">
              <AnimatedStatisticsCard
                key={card.title}
                cardData={card}
              />
            </div>
          ))}
        </div>

      </div>
      <CountryDetection />
    </div>
  );
}

export default Home;


const AnimatedStatisticsCard = ({ cardData }) => {
  const { title, value, footer, icon, color, ...rest } = cardData;

  const animatedNewValue = useCountUp(value, 1000);
  const animatedTotalValue = useCountUp(footer.value, 1500);

  return (
    <StatisticsCard
      color={color}
      {...rest}
      title={title}
      value={animatedNewValue}
      icon={React.createElement(icon, {
        className: "w-6 h-6 text-white",
      })}
      footer={
        <div className="pt-2 pb-4">
          <Typography
            variant="small"
            className="font-normal text-blue-gray-600 leading-normal"
          >
            <strong className={footer.color}>{animatedTotalValue}</strong>
            &nbsp;{footer.label}
          </Typography>
        </div>
      }
    />
  );
};